import { BarChart3, Database } from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import { ohlcvSummary } from '../node-labels'

// Base helper function for data source nodes
const createBaseDataSchema = (
  type: string,
  displayName: string,
  icon: UniversalNodeSchema['icon'],
  description: string,
): Omit<UniversalNodeSchema, 'outputs' | 'parameters'> => ({
  type,
  displayName,
  category: 'data',
  icon,
  description,
  inputs: [], // Data sources have no inputs
})

// Price Data Schema
const priceDataSchema: UniversalNodeSchema = {
  ...createBaseDataSchema(
    'priceData',
    'Price Data (OHLCV)',
    BarChart3,
    'Historical price data with Open, High, Low, Close, Volume',
  ),
  face: { shortName: 'OHLCV-1m', summary: ohlcvSummary },
  // Optional, and optional by construction: `requiredWhen` makes the input
  // load-bearing only once `symbolSource` says 'universe'. Every strategy that
  // exists today has no stored `symbolSource`, so the validator falls back to
  // the schema default 'manual' and the dangling input is not flagged.
  inputs: [
    {
      id: 'universe',
      // Deliberately unlabelled: the source control already reads "Stock
      // Selection" from the connected node, so a label beside it just says it
      // twice. An empty displayName is the supported way to suppress it.
      displayName: '',
      acceptedTypes: ['array'],
      acceptedSources: ['universe'],
      requiredWhen: { parameter: 'symbolSource', is: ['universe'] },
      // The full-width source control, not the data category's old pill
      // (Chris, 2026-10-28: no node keeps it). Optional, so unwired it is a
      // quiet prompt, never the red of a missing input.
      control: 'source',
      emptyText: 'Connect a Stock Selection',
    },
  ],
  outputs: [
    {
      id: 'ohlcv',
      displayName: 'OHLCV Data',
      type: 'ohlcv',
    },
  ],
  parameters: [
    {
      // Where the previewed ticker and day come from. Hidden on purpose: the
      // connection already answers this, so a dropdown asking again is a second
      // source of truth the user has to keep in sync. It exists because
      // `requiredWhen` needs a parameter to key on, which is what keeps the
      // universe input optional for every strategy that predates it.
      name: 'symbolSource',
      displayName: 'Ticker source',
      type: 'select',
      default: 'manual',
      options: [
        { label: 'Type a ticker', value: 'manual' },
        { label: 'Pick from universe', value: 'universe' },
      ],
      hidden: true,
    },
    {
      // The run's ticker and day while no Stock Selection is connected. Once
      // one is, it owns them (its Preview rows; Chris, 2026-10-28) and these
      // give way, here on the Price Data it wires into and, through the
      // editor's run-day context, on every other one. A run copies the Stock
      // Selection's onto each Price Data as it is sent (run-ticker-day.ts).
      name: 'symbol',
      displayName: 'Symbol',
      type: 'text',
      format: 'ticker',
      default: 'AAPL',
      hiddenWhenConnected: 'universe',
    },
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: '1m',
      options: [
        { label: '1m', value: '1m' },
        { label: '2m', value: '2m' },
        { label: '3m', value: '3m' },
        { label: '5m', value: '5m' },
        { label: '10m', value: '10m' },
        { label: '15m', value: '15m' },
        { label: '30m', value: '30m' },
        { label: '1h', value: '1h' },
        { label: '1D', value: '1D' },
      ],
    },
    {
      name: 'targetDate',
      displayName: 'Target Date',
      type: 'date',
      default: '2024-12-31',
      hiddenWhenConnected: 'universe',
    },
    {
      // When true, include pre-market (from 04:00) + after-hours (to 20:00 ET)
      // bars; when false, only regular trading hours (09:30–16:00 ET). Defaults
      // on so backtests see the full session unless the user opts out. The flag
      // serializes via node.data.parameters into the run payload; the backtest
      // data loader in RetroStockV3 must read it (see docs/nautilus/extended-hours.md).
      name: 'extendedHours',
      displayName: 'Extended Hours',
      type: 'boolean',
      default: true,
    },
    {
      // Link this Price Data node to one of the chart cells in the current
      // layout. When set (1–9), the linked cell pulls its symbol, timeframe,
      // and target date from this node instead of its own per-cell timeframe.
      // 'none' = unlinked (chart cell uses its own settings).
      //
      // The full 1–9 options are always present in the dropdown; the renderer
      // filters them down to the actual cell count of the current layout
      // (max 3×3 = 9). Layout shrinks reset any out-of-range link back to
      // 'none' via an effect in editor-page. Collisions (two nodes linking to
      // the same chart) auto-reset the previous owner — last write wins.
      name: 'linkedChartIndex',
      displayName: 'Chart Link',
      type: 'select',
      default: 'none',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Chart 1', value: '1' },
        { label: 'Chart 2', value: '2' },
        { label: 'Chart 3', value: '3' },
        { label: 'Chart 4', value: '4' },
        { label: 'Chart 5', value: '5' },
        { label: 'Chart 6', value: '6' },
        { label: 'Chart 7', value: '7' },
        { label: 'Chart 8', value: '8' },
        { label: 'Chart 9', value: '9' },
      ],
    },
  ],
}

// Reference Data Schema
const marketDataSchema: UniversalNodeSchema = {
  ...createBaseDataSchema(
    'marketData',
    'Reference Data',
    Database,
    'Reference data source for trading strategies',
  ),
  face: { shortName: 'Market Data', summary: ohlcvSummary },
  outputs: [
    {
      id: 'ohlcv',
      displayName: 'OHLCV Data',
      type: 'ohlcv',
    },
  ],
  parameters: [
    {
      name: 'symbol',
      displayName: 'Symbol',
      type: 'text',
      format: 'ticker',
      default: 'SPY',
    },
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: '1D',
      options: [
        { label: '1m', value: '1m' },
        { label: '2m', value: '2m' },
        { label: '3m', value: '3m' },
        { label: '5m', value: '5m' },
        { label: '10m', value: '10m' },
        { label: '15m', value: '15m' },
        { label: '30m', value: '30m' },
        { label: '1h', value: '1h' },
        { label: '1D', value: '1D' },
      ],
    },
    {
      // Hidden: every run sets it to the run's day (`withRunTickerDay`,
      // utils/run-ticker-day.ts), so a value typed here would do nothing. Kept
      // as a parameter because saved strategies and the engine payload carry it.
      name: 'targetDate',
      displayName: 'Target Date',
      type: 'date',
      default: '2024-12-31',
      hidden: true,
    },
  ],
}

// Array of data source schemas for registration
export const dataSchemas: UniversalNodeSchema[] = [priceDataSchema, marketDataSchema]

// Registration function
export function registerDataSchemas() {
  // console.log('Registering Data Source Node Schemas...');

  dataSchemas.forEach((schema) => {
    UniversalNodeRegistry.register(schema)
    // console.log(`Registered data schema: ${schema.type}`);
  })

  // console.log(`Registered ${dataSchemas.length} Data Source Node Schemas:`,
  //   'Price Data, Market Data'
  // );
}
